import React, { useState } from "react";
import PageLayout from "../components/layout/PageLayout";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import SectionDivider from "../components/decor/SectionDivider";
import CTASection from "../components/shared/CTASection";
import Mandala from "../components/decor/Mandala";
import { PUBLICATIONS } from "../data/publications";
import { IMG } from "../data/site";
import { BookOpen, Search, Download, Star } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const Publications = () => {
  const { t, lang } = useLang();
  const pick = (o, k) => (lang === "kn" && o[`${k}Kn`]) || o[k];
  const [q, setQ] = useState("");
  const featured = PUBLICATIONS.find((p) => p.featured);
  const filtered = PUBLICATIONS.filter((p) => !p.featured && (`${p.title} ${p.type}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <PageLayout>
      <PageHero
        eyebrow={t("publications.hero.eyebrow")}
        title={t("publications.hero.title")}
        subtitle={t("publications.hero.subtitle")}
        image={IMG.manuscripts}
      />

      {/* Featured */}
      {featured && (
        <section className="py-16" data-testid="pub-featured">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
            <div className="relative">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl relative bg-[#3B2A1A]">
                <img
                  src={require("../assets/s3umyq37_Tatvamallika_Publication.webp")}
                  alt="Tatvamallika"
                  className="w-full h-full object-cover"
                />
                {/* Top scrim for title readability */}
                <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-[#3B2A1A]/90 via-[#3B2A1A]/60 to-transparent" />
                {/* Bottom scrim for tag readability */}
                <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-[#3B2A1A]/85 to-transparent" />
                <div className="absolute inset-0 p-10 flex flex-col justify-between">
                  <div>
                    <Star className="w-8 h-8 text-[#F4CB50]" />
                    <div className="mt-6 overline text-[#F4CB50]">{pick(featured, "type")}</div>
                    <h3 className="mt-2 font-serif-display text-4xl text-[#FBF7F0] drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">{pick(featured, "title")}</h3>
                  </div>
                  <div>
                    <div className="text-xs text-[#F4CB50]">{pick(featured, "language")}</div>
                    <div className="mt-2 font-cinzel text-xs tracking-widest text-[#FBF7F0]">{lang === "kn" ? "ಮುಖ್ಯ ಪ್ರಕಟಣೆ" : "The flagship publication"}</div>
                  </div>
                </div>
                <div className="absolute -top-8 -right-8 opacity-30"><Mandala size={200} color="#D4AF37" opacity={1} /></div>
              </div>
            </div>
            <div>
              <div className="overline text-[#B65A14] mb-3">{t("publications.featured")}</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3B2A1A] leading-tight">{pick(featured, "title")}</h2>
              <div className="mt-4 gold-underline" />
              <p className="mt-6 text-[#3B2A1A]/85 leading-relaxed">{pick(featured, "desc")}</p>
              <p className="mt-4 text-[#3B2A1A]/85 leading-relaxed">{lang === "kn" ? "ಪ್ರತಿ ಸಂಚಿಕೆಯಲ್ಲಿ ದ್ವೈತ ಸಿದ್ಧಾಂತದ ಮೇಲಿನ ಶಾಸ್ತ್ರೀಯ ಲೇಖನಗಳು, ಉತ್ಸವ ವರದಿಗಳು, ಪೀಠಾಧಿಪತಿಗಳ ಪ್ರವಚನಗಳು, ಹಿರಿಯ ವಿದ್ವಾಂಸರ ಕೊಡುಗೆಗಳು ಹಾಗೂ ಮಠದ ಚಟುವಟಿಕೆಗಳ ಇತ್ತೀಚಿನ ಮಾಹಿತಿ ಇರುತ್ತದೆ." : "Every issue features scholarly articles on Dvaita philosophy, festival reports, discourses of the Peethadhipati, contributions from senior Vidwans, and updates from the Matha's activities."}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button className="inline-flex items-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-6 py-3 text-sm font-semibold" data-testid="pub-subscribe">{t("publications.subscribe")}</button>
                <button className="inline-flex items-center gap-2 rounded-full border border-[#B65A14]/40 text-[#B65A14] px-6 py-3 text-sm font-semibold" data-testid="pub-archive">{t("publications.viewArchive")}</button>
              </div>
            </div>
          </div>
        </section>
      )}

      <SectionDivider variant="mandala" />

      {/* Library */}
      <section className="pb-24" data-testid="pub-library">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
            <SectionHeading eyebrow={t("publications.explore.eyebrow")} title={t("publications.explore.title")} />
            <div className="flex items-center gap-3 bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-full px-5 py-3 w-full md:w-80">
              <Search className="w-4 h-4 text-[#B65A14]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "kn" ? "ಪ್ರಕಟಣೆಗಳನ್ನು ಹುಡುಕಿ…" : "Search publications…"} className="bg-transparent outline-none flex-1 text-sm text-[#3B2A1A]" data-testid="pub-search" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p) => (
              <div key={p.title} className="relative bg-gradient-to-br from-[#F2E7D5] to-[#FBF7F0] border border-[#D4AF37]/40 rounded-2xl p-7 card-lift overflow-hidden" data-testid={`pub-${p.title.toLowerCase().replace(/\W/g, "-")}`}>
                <div className="absolute -top-6 -right-6 opacity-15"><Mandala size={100} color="#B65A14" opacity={1} /></div>
                <BookOpen className="w-8 h-8 text-[#B65A14] mb-4" />
                <div className="overline text-[#B65A14]">{pick(p, "type")}</div>
                <h3 className="mt-2 font-serif-display text-xl text-[#3B2A1A] leading-tight">{pick(p, "title")}</h3>
                <p className="mt-3 text-sm text-[#3B2A1A]/80 leading-relaxed">{pick(p, "desc")}</p>
                <div className="mt-5 flex items-center justify-between text-xs">
                  <span className="text-[#3B2A1A]/60 italic">{pick(p, "language")}</span>
                  <button className="inline-flex items-center gap-1 text-[#B65A14] font-semibold" data-testid={`pub-download-${p.title.slice(0, 10).toLowerCase()}`}><Download className="w-3.5 h-3.5" /> {lang === "kn" ? "ವೀಕ್ಷಿಸಿ" : "View"}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow={lang === "kn" ? "ವಿದ್ವತ್ತೆಗೆ ಬೆಂಬಲ ನೀಡಿ" : "Support scholarship"}
        title={lang === "kn" ? "ಶಾಸ್ತ್ರೀಯ ಗ್ರಂಥದ ವಿಮರ್ಶಾತ್ಮಕ ಆವೃತ್ತಿಗೆ ದೇಣಿಗೆ ನೀಡಿ" : "Fund a critical edition of a classical work"}
        primary={{ label: lang === "kn" ? "ಪ್ರಕಟಣಾ ಸೇವೆ" : "Publication Seva", to: "/sevas" }}
        secondary={{ label: lang === "kn" ? "ಸಂಶೋಧನೆ" : "Research", to: "/publications" }}
        image={IMG.library}
      />
    </PageLayout>
  );
};

export default Publications;
